import { initializeApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  signInWithCustomToken,
  deleteUser,
  signInAnonymously,
  linkWithCredential,
  linkWithPopup,
  unlink,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { createGoogleAuthProvider } from "./createGoogleAuthProvider";
import {
  googleEmailMatchesAccount,
  googleLinkErrorMessage,
  googleProviderEmail,
} from "./googleAccountLink";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { LOCALSTORAGE_KEYS } from "../constants/users";
import { isDevBigFrogMockSession } from "./devAutoLogin";

const isTestEnv = false;

const API_KEY = isTestEnv
  ? "AIzaSyB46-j4jz_9SAW9jb26jvxn7XpW7lAS90E"
  : "AIzaSyBBelgl_vhZiTnUvxL5U_TAD9vopqQbC8I";
const PROJECT_NAME = isTestEnv
  ? "riverr-enterprise-test"
  : "riverr-enterprise-user";
const MESSAGING_SENDER_ID = isTestEnv ? "689886529820" : "1032772938785";
const APP_ID = isTestEnv
  ? "1:689886529820:web:2bc41f55b64f36bb9092d9"
  : "1:1032772938785:web:b3a609881b413fdac44760";
const MEASUREMENT_ID = isTestEnv ? "G-1VE5XH299X" : "G-CLLNW3M6EB";

const firebaseConfig = {
  apiKey: API_KEY,
  authDomain: `${PROJECT_NAME}.firebaseapp.com`,
  databaseURL: `https://${PROJECT_NAME}.firebaseio.com`,
  projectId: PROJECT_NAME,
  storageBucket: `${PROJECT_NAME}.appspot.com`,
  messagingSenderId: MESSAGING_SENDER_ID,
  appId: APP_ID,
  measurementId: MEASUREMENT_ID,
};

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);
const functions = getFunctions(firebaseApp);

class FirebaseAuthBackend {
  constructor() {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        localStorage.setItem("authUser", JSON.stringify(user));
        // Theme editing ticket, ready before any theme editor link is used.
        import("../api/themeTicket")
          .then((m) => m.ensureThemeTicket())
          .catch(() => {});
      } else if (!isDevBigFrogMockSession()) {
        localStorage.removeItem("authUser");
        try {
          localStorage.removeItem("rv_theme_ticket");
        } catch (e) {
          /* ignore */
        }
      }
    });
  }

  registerUser = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      return userCredential.user;
    } catch (error) {
      throw this._handleError(error);
    }
  };

  deleteAuthUser = async (user) => {
    try {
      await deleteUser(user);
      return true;
    } catch (error) {
      console.error("Failed to delete auth user:", error.message);
      throw this._handleError(error);
    }
  };

  loginUser = async (email, password, customToken = null) => {
    try {
      // Ensure any previous session is cleared before login
      await this.logout();

      if (customToken) {
        const userCredential = await signInWithCustomToken(auth, customToken);
        localStorage.setItem("authUser", JSON.stringify(userCredential.user));
        return userCredential.user;
      }

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      localStorage.setItem("authUser", JSON.stringify(userCredential.user));
      return userCredential.user;
    } catch (error) {
      throw this._handleError(error);
    }
  };

  forgetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      throw this._handleError(error);
    }
  };

  logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("authUser");
      localStorage.removeItem("riverrTheme");
      localStorage.removeItem(LOCALSTORAGE_KEYS.mockUser);
      return true;
    } catch (error) {
      throw this._handleError(error);
    }
  };

  loginAnonymously = async () => {
    try {
      const userCredential = await signInAnonymously(auth);
      localStorage.setItem("authUser", JSON.stringify(userCredential.user));
      return userCredential.user;
    } catch (error) {
      console.error("Anonymous login error:", error.code, error.message);

      // Check if anonymous auth is disabled
      if (error.code === "auth/admin-restricted-operation") {
        throw new Error(
          "Anonymous authentication is not enabled in Firebase Console. Please enable it in the Firebase Authentication settings."
        );
      }

      throw this._handleError(error);
    }
  };

  linkAnonymousWithCredential = async (email, password) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }

      if (!currentUser.isAnonymous) {
        throw new Error("Current user is not anonymous");
      }

      // Create credential
      const credential = EmailAuthProvider.credential(email, password);

      // Link anonymous account with credential
      const result = await linkWithCredential(currentUser, credential);

      // Update local storage
      localStorage.setItem("authUser", JSON.stringify(result.user));

      return result.user;
    } catch (error) {
      throw this._handleError(error);
    }
  };

  /**
   * Confirm the signed-in user's password (e.g. before revealing Team Member Pin).
   */
  reauthenticateWithPassword = async (password) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }
      const email = currentUser.email;
      if (!email) {
        throw new Error(
          "Your account has no email/password sign-in. Set a password in Profile to continue."
        );
      }
      const providers = (currentUser.providerData || []).map((p) => p.providerId);
      if (!providers.includes("password")) {
        throw new Error(
          "Your account signs in with Google. Set a password on your account to continue."
        );
      }
      const credential = EmailAuthProvider.credential(email, password);
      await reauthenticateWithCredential(currentUser, credential);
      return true;
    } catch (error) {
      if (error?.code === "auth/wrong-password" || error?.code === "auth/invalid-credential") {
        throw new Error("Incorrect password.");
      }
      if (error?.code === "auth/too-many-requests") {
        throw new Error("Too many attempts. Try again later.");
      }
      throw this._handleError(error);
    }
  };

  setLoggedInUser = async (user) => {
    localStorage.setItem("authUser", JSON.stringify(user));

    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        localStorage.setItem("userDoc", JSON.stringify(userDoc.data()));
      } else {
        localStorage.removeItem("userDoc");
      }
    } catch (error) {
      console.error("Error fetching user document:", error);
    }
  };

  getAuthenticatedUser = () => {
    return JSON.parse(localStorage.getItem("authUser")) || null;
  };

  isUserAnonymous = () => {
    const user = this.getAuthenticatedUser();
    return user && user.isAnonymous;
  };

  editProfileAPI = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      return userCredential.user;
    } catch (error) {
      throw this._handleError(error);
    }
  };

  hasPasswordProvider = () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;
    return (currentUser.providerData || []).some(
      (p) => p?.providerId === "password"
    );
  };

  hasGoogleProvider = () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;
    return (currentUser.providerData || []).some(
      (p) => p?.providerId === "google.com"
    );
  };

  getGoogleProviderEmail = () => googleProviderEmail(auth.currentUser);

  /**
   * Attach Google to the signed-in password account so the next login can
   * use Continue with Google. The Google inbox must match this account.
   */
  linkGoogleToCurrentUser = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Sign in again, then connect Google.");
    }
    if (this.hasGoogleProvider()) {
      return { alreadyLinked: true, email: this.getGoogleProviderEmail() };
    }

    let result;
    try {
      result = await linkWithPopup(currentUser, createGoogleAuthProvider());
    } catch (error) {
      if (error?.code === "auth/provider-already-linked") {
        return { alreadyLinked: true, email: this.getGoogleProviderEmail() };
      }
      const message = googleLinkErrorMessage(error);
      if (!message) return { cancelled: true };
      throw new Error(message);
    }

    const googleEmail = googleProviderEmail(result.user);
    const accountEmail = String(currentUser.email || "").trim();
    if (!googleEmailMatchesAccount(googleEmail, accountEmail)) {
      try {
        await unlink(result.user, "google.com");
      } catch (_) {
        /* still report the mismatch */
      }
      throw new Error(
        `Choose the Google account for ${accountEmail}. That Google inbox did not match.`
      );
    }

    localStorage.setItem("authUser", JSON.stringify(result.user));
    return { alreadyLinked: false, email: googleEmail };
  };

  getReauthMethods = () => ({
    password: this.hasPasswordProvider(),
    google: this.hasGoogleProvider(),
  });

  /**
   * Step-up reauth for Google accounts (reveal API key, rotate, etc.).
   * Forces the account picker so a cached Google session is not enough.
   */
  reauthenticateWithGoogle = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }
      if (!this.hasGoogleProvider()) {
        throw new Error("This account is not signed in with Google.");
      }
      await reauthenticateWithPopup(currentUser, createGoogleAuthProvider());
      return true;
    } catch (error) {
      if (error?.code === "auth/popup-closed-by-user") {
        throw new Error("Google confirmation was cancelled.");
      }
      if (error?.code === "auth/popup-blocked") {
        throw new Error("Allow popups to confirm with Google.");
      }
      if (error?.code === "auth/user-mismatch") {
        throw new Error(
          "That Google account does not match the one signed in here."
        );
      }
      if (error?.code === "auth/too-many-requests") {
        throw new Error("Too many attempts. Try again later.");
      }
      throw this._handleError(error);
    }
  };

  /**
   * Handle the error
   * @param {*} error
   * @returns {Error} - Returns an Error object for proper error handling
   */
  _handleError(error) {
    // Return an Error object instead of just a string
    // This ensures proper error catching in try/catch blocks
    if (error instanceof Error) {
      return error;
    }
    return new Error(error.message || error || "An unknown error occurred");
  }
}

let _fireBaseBackend = null;

/**
 * Initilize the backend
 */
const initFirebaseBackend = () => {
  if (!_fireBaseBackend) {
    _fireBaseBackend = new FirebaseAuthBackend();
  }
  return _fireBaseBackend;
};

/**
 * Returns the firebase backend
 */
const getFirebaseBackend = () => {
  return _fireBaseBackend;
};

initFirebaseBackend();

export {
  firebaseConfig,
  getFirebaseBackend,
  initFirebaseBackend,
  auth,
  db as projectFirestore,
  storage as projectStorage,
  functions as projectFunctions,
};
